/**
 * Editable concurrency-cap configuration (operator ask 2026-09-18: "these should all be
 * parameters exposed in the UI that I can change, because I'll change Gemini's one").
 *
 * Sibling of ConcurrencyCapsPanel, deliberately a SEPARATE component: that one renders
 * live READINGS (cap vs active right now, polled on a timer); this one is the
 * CONFIGURATION behind them, written through PUT/DELETE
 * /api/accounts/concurrency-cap-knobs. Keeping them apart means a poll tick can never
 * clobber a half-typed value in an input.
 *
 * Provenance is surfaced per row rather than just the number: an override and a shipped
 * default look identical once applied, and these knobs get turned during incidents, so
 * "is this value someone's deliberate change or what we shipped?" has to be answerable
 * at a glance -- hence the `overridden` badge and the always-visible default.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import type { ConcurrencyCapKnobView } from "./types";

const SCOPE_LABEL: Record<string, string> = {
  account: "per-account",
  fleet: "fleet-wide",
  project: "per-project",
};

/** Group knobs by provider, preserving the server's ordering within each group. */
export function groupByProvider(rows: ConcurrencyCapKnobView[]): [string, ConcurrencyCapKnobView[]][] {
  const out = new Map<string, ConcurrencyCapKnobView[]>();
  for (const row of rows) {
    const bucket = out.get(row.provider);
    if (bucket) bucket.push(row);
    else out.set(row.provider, [row]);
  }
  return [...out.entries()];
}

/** A draft is saveable only when it is a whole number >= 1 (every underlying Field is
 * `ge=1`) AND actually differs from what the server currently enforces. */
export function draftState(draft: string, effective: number): { valid: boolean; dirty: boolean; parsed: number } {
  const parsed = Number(draft);
  const valid = draft.trim() !== "" && Number.isInteger(parsed) && parsed >= 1;
  return { valid, dirty: valid && parsed !== effective, parsed };
}

function KnobRow({
  row,
  busy,
  onSave,
  onReset,
}: {
  row: ConcurrencyCapKnobView;
  busy: boolean;
  onSave: (knob: string, value: number) => void;
  onReset: (knob: string) => void;
}) {
  const [draft, setDraft] = useState<string>(String(row.effective));
  // Re-sync when the server's value changes underneath us (our own save landing, or
  // another operator's edit arriving on a refresh).
  useEffect(() => {
    setDraft(String(row.effective));
  }, [row.effective]);

  const { valid, dirty, parsed } = draftState(draft, row.effective);

  return (
    <tr>
      <td>
        {row.label}
        <div className="muted small">{SCOPE_LABEL[row.scope] ?? row.scope}</div>
      </td>
      <td>
        <input
          type="number"
          min={1}
          value={draft}
          disabled={busy}
          aria-label={`${row.label} concurrency cap`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && dirty) onSave(row.knob, parsed);
          }}
          style={{ width: "5rem" }}
        />
        {!valid && <div className="muted small">whole number, 1 or more</div>}
      </td>
      <td className="muted small">
        default {row.default}
        {row.overridden ? <span className="badge"> overridden</span> : null}
      </td>
      <td>
        <button type="button" disabled={!dirty || busy} onClick={() => onSave(row.knob, parsed)}>
          Save
        </button>{" "}
        <button type="button" disabled={!row.overridden || busy} onClick={() => onReset(row.knob)}>
          Reset
        </button>
      </td>
    </tr>
  );
}

export function ConcurrencyCapKnobsPanel({ baseUrl }: { baseUrl: string }) {
  const [rows, setRows] = useState<ConcurrencyCapKnobView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.getConcurrencyCapKnobs(baseUrl));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [baseUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = async (knob: string, fn: () => Promise<ConcurrencyCapKnobView>) => {
    setBusy(knob);
    try {
      const updated = await fn();
      setRows((prev) => (prev ?? []).map((r) => (r.knob === updated.knob ? updated : r)));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel title="Concurrency caps — configuration">
      {error ? <div className="error">{error}</div> : null}
      {rows === null ? (
        <div className="muted">Loading…</div>
      ) : (
        <>
          <p className="muted small">
            Takes effect on the next account pick — no redeploy. More accounts means more concurrency: these are
            per-account (or per-project) ceilings, not a fleet budget.
          </p>
          {groupByProvider(rows).map(([provider, group]) => (
            <div key={provider}>
              <h4>{provider}</h4>
              <table className="data-table">
                <tbody>
                  {group.map((row) => (
                    <KnobRow
                      key={row.knob}
                      row={row}
                      busy={busy === row.knob}
                      onSave={(knob, value) => void apply(knob, () => api.setConcurrencyCapKnob(baseUrl, knob, value))}
                      onReset={(knob) => void apply(knob, () => api.clearConcurrencyCapKnob(baseUrl, knob))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </Panel>
  );
}
